import type { OrderStats, Warehouse } from "@/lib/product-types";

/**
 * Единая точка расчёта колонки «Хватит дней (кластеры, 7/14/30д)».
 *
 * Метрика считается ПО КАЖДОМУ КЛАСТЕРУ: остаток (free) кластера ÷ скорость
 * заказов этого кластера (clusters_to за период ÷ дни). Значимое число —
 * МИНИМУМ по кластерам (самый критичный кластер, который кончится первым).
 *
 * Раньше это жило в трёх местах с разными формулами: тултип ячейки считал
 * минимум-по-кластеру (free), а резолвер сортировки/экспорта и SORT_GETTERS —
 * агрегат (общий запас ÷ общая скорость). Из-за этого ячейка показывала одно
 * число, а сортировка/экспорт — другое. Теперь и тултип, и резолвер берут
 * значение отсюда.
 */
export type ClusterDaysLeftRow = {
  cluster: string;
  stock: number;
  orders: number;
  speed: number;
  daysLeft: number | null; // null = в кластере нет продаж за период
};

function clusterOrdersMap(stats: OrderStats, period: 7 | 14 | 30): Record<string, number> {
  if (period === 7) return stats.clusters_to_7d ?? {};
  if (period === 14) return stats.clusters_to_14d ?? {};
  return stats.clusters_to ?? {};
}

/** Разбивка по кластерам для тултипа: остаток, заказы, скорость, дней хватит. */
export function clusterDaysLeftRows(
  warehouses: Warehouse[] | undefined,
  stats: OrderStats,
  period: 7 | 14 | 30,
): ClusterDaysLeftRow[] {
  const stockMap: Record<string, number> = {};
  for (const w of warehouses ?? []) {
    const key = w.cluster || "Без кластера";
    stockMap[key] = (stockMap[key] ?? 0) + (w.free ?? 0);
  }
  const ordersMap = clusterOrdersMap(stats, period);
  const allClusters = new Set([...Object.keys(stockMap), ...Object.keys(ordersMap)]);
  return [...allClusters]
    .map(cluster => {
      const stock = stockMap[cluster] ?? 0;
      const orders = ordersMap[cluster] ?? 0;
      const speed = orders / period;
      const daysLeft = speed > 0 ? Math.floor(stock / speed) : null;
      return { cluster, stock, orders, speed, daysLeft };
    })
    .filter(row => row.stock > 0 || row.orders > 0);
}

/**
 * Критичное (минимальное) «хватит дней» по кластерам — основное значение
 * колонки для ячейки, сортировки и экспорта. null = ни в одном кластере нет
 * продаж за период (нечего прогнозировать).
 */
export function criticalClusterDaysLeft(
  warehouses: Warehouse[] | undefined,
  stats: OrderStats,
  period: 7 | 14 | 30,
): number | null {
  let critical: number | null = null;
  for (const row of clusterDaysLeftRows(warehouses, stats, period)) {
    if (row.daysLeft === null) continue;
    if (critical === null || row.daysLeft < critical) critical = row.daysLeft;
  }
  return critical;
}
